import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CheckCircle,
  People,
  Business,
  SmartToy,
  Speed,
  Security,
  Payment
} from '@mui/icons-material';

// Import images
import logo0 from '../assets/logo0.png';
import premium8 from '../assets/premium8.png';

// Styled Components
const PremiumCard = styled(Card)(({ theme, featured }) => ({
  height: '100%',
  backgroundColor: featured ? '#000' : '#fff',
  color: featured ? '#fff' : '#000',
  border: featured ? '2px solid #FFE302' : '1px solid #e0e0e0',
  borderRadius: 0,
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)'
  }
}));

// Feature List Component
const FeatureList = ({ features, dark }) => (
  <List dense>
    {features.map((feature, index) => (
      <ListItem key={index} sx={{ py: 1 }}>
        <ListItemIcon sx={{ minWidth: 36 }}>
          <CheckCircle sx={{ color: dark ? '#FFE302' : '#00A651', fontSize: 20 }} />
        </ListItemIcon>
        <ListItemText 
          primary={feature} 
          sx={{ 
            '& .MuiListItemText-primary': {
              color: dark ? '#fff' : '#000',
              fontWeight: 500,
              fontSize: '0.9rem'
            }
          }} 
        />
      </ListItem>
    ))}
  </List>
);

// Premium Component
const Premium = () => {
  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh' }}>
      {/* Enhanced Hero Section with premium8.png */}
      <Box sx={{ 
        bgcolor: '#000', 
        py: { xs: 6, md: 8 },
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box 
                component="img"
                src={logo0}
                alt="Warrili"
                sx={{ 
                  width: '150px',
                  mb: 4,
                  filter: 'brightness(1.2)',
                  background: 'rgba(255,255,255,0.1)',
                  padding: 2,
                  borderRadius: 1
                }}
              />
              
              <Typography 
                variant="h1"
                sx={{ 
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 900,
                  color: '#FFE302',
                  mb: 3,
                  maxWidth: '800px',
                  textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                }}
              >
                Unlock Advanced Features with Premium Access
              </Typography>
              
              <Typography 
                variant="h5"
                sx={{ 
                  fontSize: { xs: '1.2rem', md: '1.5rem' },
                  fontWeight: 500,
                  color: '#fff',
                  mb: 4,
                  maxWidth: '600px',
                  opacity: 0.9
                }}
              >
                Get priority support, advanced diagnostics, and exclusive features to enhance your repair experience.
              </Typography>
              
              <Button
                variant="contained"
                sx={{
                  bgcolor: '#FFE302',
                  color: '#000',
                  px: 4,
                  py: 1.5,
                  borderRadius: 0,
                  fontWeight: 700,
                  fontSize: '1rem',
                  '&:hover': {
                    bgcolor: '#fff'
                  }
                }}
              >
                EXPLORE PREMIUM
              </Button>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative', textAlign: 'center' }}>
                <Box 
                  component="img"
                  src={premium8}
                  alt="Premium Features"
                  sx={{ 
                    maxWidth: '100%',
                    height: 'auto',
                    maxHeight: '500px',
                    filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.5))',
                    border: '4px solid rgba(255,227,2,0.3)',
                    borderRadius: 2
                  }}
                />
                
                {/* Decorative elements */}
                <Box sx={{
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  border: '2px solid #FFE302',
                  opacity: 0.3,
                  display: { xs: 'none', md: 'block' }
                }} />
                
                <Box sx={{
                  position: 'absolute',
                  bottom: -10,
                  left: '20%',
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  bgcolor: '#00A651',
                  opacity: 0.4,
                  display: { xs: 'none', md: 'block' }
                }} />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Premium Plans Section */}
      <Box sx={{ py: 8, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h2" 
            align="center"
            sx={{ 
              fontWeight: 900,
              mb: 1,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            Premium Solutions
          </Typography>
          
          <Typography 
            variant="h5" 
            align="center"
            sx={{ 
              color: '#666',
              mb: 6,
              fontWeight: 500
            }}
          >
            Choose the plan that fits your needs
          </Typography>

          <Grid container spacing={4}>
            {/* Individual Premium */}
            <Grid item xs={12} md={6}>
              <PremiumCard>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    mb: 3
                  }}>
                    <Box sx={{
                      bgcolor: '#FFE302',
                      p: 1,
                      mr: 2
                    }}>
                      <People sx={{ fontSize: 30, color: '#000' }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      Individual Premium
                    </Typography>
                  </Box>

                  <Typography variant="h4" sx={{ mb: 3, fontWeight: 800 }}>
                    2,500 DZD<Typography component="span" variant="body1">/month</Typography>
                  </Typography>

                  <FeatureList features={[
                    'Advanced AI diagnostics for personal devices',
                    'Priority 24/7 technical support',
                    'Unlimited device scans',
                    'Detailed repair history tracking',
                    'Video consultations with experts',
                    'Personal cloud storage for device data'
                  ]} />

                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      mt: 3,
                      bgcolor: '#000',
                      color: '#FFE302',
                      py: 1.5,
                      fontWeight: 700,
                      borderRadius: 0,
                      '&:hover': {
                        bgcolor: '#333'
                      }
                    }}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </PremiumCard>
            </Grid>

            {/* Business Premium */}
            <Grid item xs={12} md={6}>
              <PremiumCard featured>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    mb: 3
                  }}>
                    <Box sx={{
                      bgcolor: '#00A651',
                      p: 1,
                      mr: 2
                    }}>
                      <Business sx={{ fontSize: 30, color: '#fff' }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#FFE302' }}>
                      Business Premium
                    </Typography>
                  </Box>

                  <Typography variant="h4" sx={{ mb: 3, fontWeight: 800, color: '#fff' }}>
                    6,500 DZD<Typography component="span" variant="body1">/month</Typography>
                  </Typography>

                  <FeatureList 
                    features={[
                      'All Individual Premium features',
                      'Multi-device management dashboard',
                      'Team access controls',
                      'Bulk device diagnostics',
                      'Custom repair workflows',
                      'API access for integration',
                      'Dedicated account manager',
                      'Priority on-site support'
                    ]}
                    dark
                  />

                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      mt: 3,
                      bgcolor: '#FFE302',
                      color: '#000',
                      py: 1.5,
                      fontWeight: 700,
                      borderRadius: 0,
                      '&:hover': {
                        bgcolor: '#fff',
                        color: '#000'
                      }
                    }}
                  >
                    Contact Sales
                  </Button>
                </CardContent>
              </PremiumCard>
            </Grid>
          </Grid>

          {/* Feature Highlights */}
          <Grid container spacing={3} sx={{ mt: 6 }}>
            {[
              {
                icon: <SmartToy sx={{ fontSize: 40 }} />,
                title: 'AI-Powered Diagnostics',
                description: 'Advanced fault detection and analysis'
              },
              {
                icon: <Speed sx={{ fontSize: 40 }} />,
                title: 'Fast Resolution',
                description: 'Quick identification and repair solutions'
              },
              {
                icon: <Security sx={{ fontSize: 40 }} />,
                title: 'Secure Platform',
                description: 'Enterprise-grade security measures'
              },
              {
                icon: <Payment sx={{ fontSize: 40 }} />,
                title: 'Flexible Billing',
                description: 'Pay only for what you need'
              }
            ].map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box sx={{
                  p: 3,
                  bgcolor: '#f8f8f8',
                  height: '100%',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    '& .icon': {
                      color: '#00A651'
                    }
                  }
                }}>
                  <Box sx={{
                    color: '#000',
                    mb: 2,
                    transition: 'color 0.3s ease'
                  }} className="icon">
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {feature.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      
      {/* Call to Action Section */}
      <Box sx={{ py: 6, bgcolor: '#00A651' }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" sx={{ color: '#fff', fontWeight: 800, mb: 3 }}>
            Ready to Upgrade Your Experience?
          </Typography>
          <Typography variant="h6" sx={{ color: '#fff', opacity: 0.9, mb: 4, fontWeight: 500 }}>
            Join thousands of users who are already benefiting from our premium features
          </Typography>
          <Button 
            variant="contained" 
            sx={{ 
              bgcolor: '#FFE302',
              color: '#000',
              px: 4,
              py: 1.5,
              fontWeight: 700,
              fontSize: '1rem',
              borderRadius: 0,
              '&:hover': {
                bgcolor: '#fff'
              }
            }}
          >
            UPGRADE NOW
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default Premium;